import {capitalize, moment} from "@/src/modules/core/lib";
import {ParamsOptions} from "@/src/modules/core/types";

import {Property} from "../../properties/types";
import {Shift, ShiftProperty, ShiftType, Template} from "../types";

function getDateFromDatetime(datetime: string) {
  const date = moment(datetime).format("YYYY-MM-DD");
  return new Date(`${date}T00:00:00`);
}

export function getShiftTypeLabel(type: ShiftType): string {
  return capitalize(type);
}

export function getShiftDefaultValues(shift: Shift | null) {
  return {
    // Hydrated on edit via assignee user fetch (shift.user has no role).
    role_id: null,
    user: shift?.user
      ? {
          user_id: shift.user.id,
          first_name: shift.user.name.split(" ")[0],
          last_name: shift.user.name.split(" ")[1],
        }
      : null,
    properties: (shift?.properties?.map((property) => ({
      id: property.property.id,
      name: property.property.name,
    })) ?? []) as Property[],
    patrol_route: shift?.patrol_route
      ? {id: shift.patrol_route.patrol_route_id, name: shift.patrol_route.name}
      : null,
    from_date: shift?.from_datetime ? getDateFromDatetime(shift?.from_datetime) : undefined,
    from_time: shift?.from_datetime ? moment(shift.from_datetime).format("HH:mm") : "",
    to_date: shift?.to_datetime ? getDateFromDatetime(shift?.to_datetime) : undefined,
    to_time: shift?.to_datetime ? moment(shift.to_datetime).format("HH:mm") : "",
    type: shift?.type
      ? {label: capitalize(shift.type), value: shift.type}
      : {label: "Normal", value: ShiftType.NORMAL},
    notes: shift?.notes ?? "",
    breaks:
      shift?.breaks?.map((b) => ({
        ...b,
        start_date: getDateFromDatetime(b.start_datetime),
        start_time: moment(b.start_datetime).format("HH:mm"),
      })) ?? [],
    notify_officer: false,
    days: [],
    break_suggestion: null,
  };
}

export function getTemplateDefaultValues(template: Template | null) {
  return {
    type: template?.type
      ? {label: capitalize(template.type), value: template.type}
      : {label: "Normal", value: ShiftType.NORMAL},
    template_name: template?.template_name ?? "",
    properties: (template?.properties?.map((property) => ({
      id: property.property.id,
      name: property.property.name,
    })) ?? []) as Property[],
    from_time: template?.from_time?.slice(0, 5) ?? "",
    to_time: template?.to_time?.slice(0, 5) ?? "",
    breaks:
      template?.breaks?.map((b) => ({
        ...b,
        start_time: b.start_time.slice(0, 5),
      })) ?? [],
  };
}

/** Primary assignment first (matches admin shift details / prod). */
export function sortShiftPropertiesPrimaryFirst(
  properties: readonly ShiftProperty[],
): ShiftProperty[] {
  return [...properties].sort((a, b) => {
    const rank = (p: ShiftProperty) => (p.assignment_type === "primary" ? 0 : 1);
    return rank(a) - rank(b);
  });
}

export function formatManagePageOptions({filter, ...options}: ParamsOptions) {
  if (filter?.properties) {
    filter = {
      ...filter,
      properties: filter.properties.id,
    };
  }

  if (filter?.users) {
    filter = {
      ...filter,
      users: filter.users.user_id,
    };
  }

  if (filter?.status) {
    filter = {
      ...filter,
      status: filter.status.map((s: {value: string}) => s.value).join(","),
    };
  }

  if (filter?.type) {
    filter = {
      ...filter,
      type: filter.type.map((t: {value: string}) => t.value).join(","),
    };
  }

  return {
    ...options,
    filter,
  };
}
